import { EllipsisVertical } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useFeatureFlagEnabled } from '@/lib/posthog/hooks/useFeatureFlagEnabled';
import { AnyPermissionGate } from '@/modules/auth/components/permissions/AnyPermissionGate';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { roles } from '@/modules/auth/roles';
import { hasAnyRole } from '@/modules/auth/utils/hasRoles';
import { AddCandidatesDialog } from '@/modules/candidates/components/addCandidatesDialog/AddCandidatesDialog';
import { useDeleteJobMutation } from '@/modules/jobs/mutations/useDeleteJobMutation';
import { useSourceJobMutation } from '@/modules/jobs/mutations/useSourceJobMutation';
import { useUpdateJobMutation } from '@/modules/jobs/mutations/useUpdateJobMutation';
import type { JobApi } from '@/modules/jobs/types';
import {
  getPublicShortlistEditPath,
  getPublicShortlistUrl,
  isPublicShortlistAvailable,
} from '@/modules/public-shortlist/publicShortlist';
import { ConfirmDeleteDialog } from '@/modules/ui/components/confirm-delete-dialog/ConfirmDeleteDialog';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';
import { useDropdownOnHover } from '@/modules/ui/components/dropdown-menu/useDropdownOnHover';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface JobDropdownProps {
  job: JobApi;
  onDeleted?: () => void;
}

type ConfirmDialogType = 'publish' | 'runSourcing' | null;

export const JobDropdown = ({ job, onDeleted }: JobDropdownProps) => {
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogType>(null);

  const {
    isOpen: isDropdownOpen,
    setIsOpen: setIsDropdownOpen,
    handleOpen: handleDropdownOpen,
    handleClose: handleDropdownClose,
  } = useDropdownOnHover();

  const deleteJobMutation = useDeleteJobMutation();
  const sourceJobMutation = useSourceJobMutation();
  const updateJobMutation = useUpdateJobMutation();
  const isSourcingEnabled = useFeatureFlagEnabled('sourcing');
  const isSourcingContinuation =
    job.sourcingState === 'Finished' || job.sourcingState === 'Failed';

  const currentUserQuery = useCurrentUserQuery();
  const canViewHMDashboard = hasAnyRole(
    [roles.Recruiter, roles.Admin, roles.SuperAdmin],
    currentUserQuery.data?.roles ?? [],
  );

  const hmDashboardParams = new URLSearchParams({
    tenantId: job.companyId,
    jobs: job.id,
  });
  const hmDashboardHref = `/hm?${hmDashboardParams.toString()}`;

  const handleDelete = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteJobMutation.mutate(job.id, {
      onSuccess: () => {
        toast.success(`Job ${job.title} has been deleted`);
        onDeleted?.();
        setIsDeleteDialogOpen(false);
      },
      onError: (error) => {
        toast.error(
          `Job ${job.title} has not been deleted: ${extractErrorMessage(error)}`,
        );
      },
    });
  };

  const runSourcing = () => {
    if (!isSourcingEnabled) {
      return;
    }

    sourceJobMutation.mutate(
      {
        jobId: job.id,
        continueSourcing: isSourcingContinuation,
      },
      {
        onSuccess: () => {
          toast.success('Job sourcing has been started');
          setConfirmDialog(null);
        },
        onError: (error) => {
          toast.error(
            `Failed to start job sourcing: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleRunSourcing = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setConfirmDialog('runSourcing');
  };

  const handleAddCandidates = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    setIsUploadDialogOpen(true);
  };

  const updateJobState = (newJobState: 'Published' | 'Unpublished') => {
    updateJobMutation.mutate(
      {
        jobId: job.id,
        body: {
          ...job,
          niceToHaveSkills: job.niceToHaveSkills ?? [],
          state: newJobState,
        },
      },
      {
        onSuccess: () => {
          toast.success(
            `Job ${job.title} has been ${newJobState === 'Published' ? 'published' : 'unpublished'}`,
          );
          setIsDropdownOpen(false);
          if (newJobState === 'Published') {
            setConfirmDialog(null);
          }
        },
        onError: (error) => {
          toast.error(
            `Failed to update job state: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleToggleState = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    const isPublished = job.state === 'Published';
    if (isPublished) {
      updateJobState('Unpublished');
      return;
    }

    setConfirmDialog('publish');
  };

  const handlePublicShortlist = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    if (!isPublicShortlistAvailable(job)) {
      navigate(getPublicShortlistEditPath(job.id));
      return;
    }

    const publicUrl = getPublicShortlistUrl(job);
    if (!publicUrl) {
      navigate(getPublicShortlistEditPath(job.id));
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public shortlist URL copied');
      setIsDropdownOpen(false);
    } catch {
      toast.error('Failed to copy public shortlist URL');
    }
  };

  const isRunSourcingVisible =
    isSourcingEnabled &&
    (isSourcingContinuation || job.sourcingState === 'Draft');

  return (
    <AnyPermissionGate
      permissions={[
        ...(isSourcingEnabled ? [permissions.Jobs.StartSourcing] : []),
        permissions.CandidateJobs.ManualUpload,
        permissions.Jobs.Update,
        permissions.Jobs.Create,
        permissions.Jobs.Delete,
      ]}
    >
      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        description={`This action cannot be undone. This permanently deletes the ${job.title} job.`}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onDelete={handleConfirmDelete}
      />
      <ConfirmDialog
        open={confirmDialog === 'runSourcing'}
        onOpenChange={(open) => !open && setConfirmDialog(null)}
        title="Run sourcing?"
        description={`Are you sure you want to run sourcing for ${job.title}?`}
        confirmText="Run sourcing"
        onConfirm={runSourcing}
        onCancel={() => setConfirmDialog(null)}
      />
      <ConfirmDialog
        open={confirmDialog === 'publish'}
        onOpenChange={(open) => !open && setConfirmDialog(null)}
        title="Publish job?"
        description={`Are you sure you want to publish ${job.title}? This will make job visible and send Slack to hiring managers.`}
        confirmText="Publish"
        onConfirm={() => updateJobState('Published')}
        onCancel={() => setConfirmDialog(null)}
      />

      <AddCandidatesDialog
        job={job}
        open={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
        onSuccess={() => setIsUploadDialogOpen(false)}
        onCancel={() => setIsUploadDialogOpen(false)}
      />

      <DropdownMenu
        open={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        modal={false}
      >
        <DropdownMenuTrigger asChild>
          <div
            className="cursor-pointer"
            onMouseEnter={handleDropdownOpen}
            onMouseLeave={handleDropdownClose}
          >
            <EllipsisVertical size={22} className="text-primary-700" />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          onMouseEnter={handleDropdownOpen}
          onMouseLeave={handleDropdownClose}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          <PermissionsGate permissions={[permissions.Jobs.Update]}>
            <DropdownMenuItem asChild>
              <Link to={`/jobs/${job.id}/edit`}>Edit</Link>
            </DropdownMenuItem>
          </PermissionsGate>

          {isRunSourcingVisible && (
            <PermissionsGate
              permissions={[
                permissions.Jobs.StartSourcing,
                ...(isSourcingContinuation
                  ? [permissions.Jobs.ContinueSourcing]
                  : []),
              ]}
            >
              <DropdownMenuItem onClick={handleRunSourcing}>
                Run sourcing
              </DropdownMenuItem>
            </PermissionsGate>
          )}

          <PermissionsGate permissions={[permissions.Files.Write]}>
            <DropdownMenuItem onClick={handleAddCandidates}>
              Add candidates
            </DropdownMenuItem>
          </PermissionsGate>

          <PermissionsGate permissions={[permissions.Jobs.Create]}>
            <DropdownMenuItem asChild>
              <Link to={`/jobs/${job.id}/duplicate`}>Duplicate</Link>
            </DropdownMenuItem>
          </PermissionsGate>

          <PermissionsGate permissions={[permissions.Jobs.Update]}>
            <DropdownMenuItem onClick={handleToggleState}>
              {job.state === 'Published' ? 'Unpublish' : 'Publish'}
            </DropdownMenuItem>
          </PermissionsGate>

          <PermissionsGate permissions={[permissions.Jobs.Update]}>
            <DropdownMenuItem onClick={handlePublicShortlist}>
              {isPublicShortlistAvailable(job)
                ? 'Copy public shortlist URL'
                : 'Enable public shortlist'}
            </DropdownMenuItem>
          </PermissionsGate>

          {canViewHMDashboard && (
            <DropdownMenuItem asChild>
              <Link to={hmDashboardHref}>View HM Dashboard</Link>
            </DropdownMenuItem>
          )}

          <PermissionsGate permissions={[permissions.Jobs.Delete]}>
            <DropdownMenuItem variant="destructive" onClick={handleDelete}>
              Delete
            </DropdownMenuItem>
          </PermissionsGate>
        </DropdownMenuContent>
      </DropdownMenu>
    </AnyPermissionGate>
  );
};
